import { autoUpdate, computePosition, flip, offset, Placement, shift } from '@floating-ui/dom';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './Popover.scss';

@customElement('app-popover')
export class AppPopoverComponent extends LitElement {
    /**
     * Indicador booleano que refleja si el popover está abierto.
     * Se mantiene la propiedad interna `open` por compatibilidad con Lit.
     */
    @property({ type: Boolean, reflect: true }) open = false;

    /**
     * Posición preferida del popover según @floating-ui (p.ej. 'bottom-start').
     * Propiedad interna `placement` mantenida por compatibilidad.
     */
    @property({ type: String }) placement: Placement = 'bottom-start';

    /**
     * Desplazamiento en píxeles entre el elemento de referencia y el popover.
     * Propiedad interna `offset` mantenida por compatibilidad.
     */
    @property({ type: Number }) offset = 8;

    // Elemento de referencia (target) usado para el posicionamiento
    private referenceEl: HTMLElement | null = null;
    private cleanupAutoUpdate: (() => void) | null = null;

    constructor() {
        super();
        this.classList.add('app-popover--hidden'); // oculto por defecto para evitar que el contenido inyectado sea visible antes del primer mostrar()
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this;
    }

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('mousedown', this.onDocumentClick);
        window.addEventListener('keydown', this.onKeyDown);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this.onDocumentClick);
        window.removeEventListener('keydown', this.onKeyDown);
        this.stopAutoUpdate();
    }

    render() {
        // No renderizar contenedores internos: el consumidor inyecta contenido
        // directamente en el host (Light DOM).
        return html``;
    }

    /**
     * Mostrar el popover cerca del elemento de referencia.
     * @param reference Elemento de referencia o selector CSS.
     * @returns Promise<void> que se resuelve cuando el popover se ha mostrado y posicionado.
     */
    public async show(reference: HTMLElement | string) {
        if (typeof reference === 'string') {
            this.referenceEl = document.querySelector(reference) as HTMLElement | null;
        } else {
            this.referenceEl = reference;
        }

        if (!this.referenceEl) {
            console.warn('app-popover: reference element not found');
            return;
        }

        // Asegura de que el popover esté en el body para evitar problemas de overflow y posicionamiento
        if (this.parentElement !== document.body) {
            document.body.appendChild(this);
        }

        this.open = true;
        // asegurar que el host sea visible
        this.classList.remove('app-popover--hidden');
        // No esperamos el ciclo de render de Lit porque este componente
        // deliberadamente evita que Lit escriba en el Light DOM del host
        // (ver shouldUpdate()).
        this.startAutoUpdate();
    }

    public hide() {
        this.open = false;
        this.stopAutoUpdate();
        this.classList.add('app-popover--hidden');
    }

    public toggle(reference?: HTMLElement | string) {
        if (this.open) this.hide(); else this.show(reference);
    }

    private startAutoUpdate() {
        if (!this.referenceEl) return;
        this.stopAutoUpdate();
        const update = async () => {
            await computePosition(this.referenceEl as HTMLElement, this as HTMLElement, {
                placement: this.placement,
                middleware: [offset(this.offset), flip(), shift({ padding: 8 })],
            }).then(({ x, y }) => {
                const el = this as HTMLElement;
                if (el) {
                    Object.assign(el.style, {
                        left: `${Math.round(x)}px`,
                        top: `${Math.round(y)}px`,
                        position: 'fixed',
                    });
                    // asegurar que el host esté posicionado y en el orden de apilamiento (z-index)
                    el.style.zIndex = el.style.zIndex || '9999';
                }
            });
        };

        // autoUpdate devuelve una función de limpieza
        this.cleanupAutoUpdate = autoUpdate(this.referenceEl as HTMLElement, this as HTMLElement, update);
        // Llamar inmediatamente para establecer la posición inicial
        update();
    }

    private stopAutoUpdate() {
        if (this.cleanupAutoUpdate) {
            this.cleanupAutoUpdate();
            this.cleanupAutoUpdate = null;
        }
    }

    // Un clic fuera cierra el popover
    private onDocumentClick(ev: MouseEvent) {
        if (!this.open) return;
        const path = ev.composedPath ? ev.composedPath() : (ev as any).path || [];
        const clickedInside = path.includes(this) || (this.referenceEl && path.includes(this.referenceEl));
        if (!clickedInside) this.hide();
    }

    private onKeyDown(ev: KeyboardEvent) {
        if (ev.key === 'Escape' && this.open) this.hide();
    }

    // No hay distribución: el consumidor coloca contenido directamente dentro
    // de `<app-popover>` en el Light DOM.
    // Evitar que Lit re-renderice el host y elimine los hijos inyectados.
    // Gestionamos el DOM y el posicionamiento de forma manual.
    protected shouldUpdate(_changedProps: Map<string | number | symbol, unknown>): boolean {
        return false;
    }

    // /* ---------------------- API pública en español ---------------------- */
    // /**
    //  * Mostrar el popover cerca del elemento de referencia.
    //  * Alias en español de `show()`.
    //  */
    // public mostrar(reference: HTMLElement | string) {
    //     return this.show(reference);
    // }

    // /**
    //  * Ocultar el popover.
    //  * Alias en español de `hide()`.
    //  */
    // public ocultar() {
    //     return this.hide();
    // }

    // /**
    //  * Alternar visibilidad del popover.
    //  * Alias en español de `toggle()`.
    //  */
    // public alternar(reference?: HTMLElement | string) {
    //     return this.toggle(reference);
    // }

    // /**
    //  * API: Propiedad `visible` para consultar si está abierto.
    //  * Asigna `true`/`false` para mostrar/ocultar (sin posicionar automáticamente).
    //  */
    // public get visible() {
    //     return this.open;
    // }
    // public set visible(v: boolean) {
    //     if (v) this.classList.remove('app-popover--hidden'); else this.classList.add('app-popover--hidden');
    //     this.open = !!v;
    //     if (!v) this.stopAutoUpdate();
    // }

    // /**
    //  * API: Propiedad `posicion` que mapea a `placement` de @floating-ui.
    //  */
    // public get posicion() {
    //     return this.placement;
    // }

    // public set posicion(p: Placement) {
    //     this.placement = p;
    // }

    // /**
    //  * API: Propiedad `desplazamiento` que mapea a `offset`.
    //  */
    // public get desplazamiento() {
    //     return this.offset;
    // }
    // public set desplazamiento(n: number) {
    //     this.offset = n;
    // }
}

declare global {
    interface HTMLElementTagNameMap {
        'app-popover': AppPopoverComponent;
    }
}
